import { getFonts, getElement, getMdElement } from 'store/modules/site';

const DEFAULT_FONT_FAMILY = 'Montserrat';

const createElementStylesMap = ({
  fonts, getProps, defaultFont,
}) => (obj, element) => {
  const props = getProps(element);

  const {
    color, weight, lineHeight, fontSize, backgroundColor, textAlign,
    textTransform, borderRadius, letterSpacing, textDecoration, position,
  } = props;

  const textColor = color;
  const fontLevel = props.font || 'primary';
  const font = fonts[fontLevel]?.name
    ? `${fonts[fontLevel].name}, sans-serif`
    : `${DEFAULT_FONT_FAMILY}, Helvetica, sans-serif`;

  const important = element === 'a' ? ';' : ' !important;';

  return {
    ...obj,
    [element]: [
      defaultFont && `font-family: ${font}${important}`,
      fontSize && `font-size: ${fontSize}${important}`,
      lineHeight && `line-height: ${lineHeight}${important}`,
      letterSpacing && `letter-spacing: ${letterSpacing}${important}`,
      weight && `font-weight: ${weight}${important}`,
      textColor && `color: ${textColor}${important}`,
      backgroundColor && `background-color: ${backgroundColor}${important}`,
      textAlign && `text-align: ${textAlign}${important}`,
      textTransform && `text-transform: ${textTransform}${important}`,
      textDecoration && `text-decoration: ${textDecoration}${important}`,
      borderRadius && `border-radius: ${borderRadius}${important}`,
      position && `position: ${position}${important}`,
    ].filter(Boolean).join(' '),
  };
};

export default (state) => {
  const fonts = getFonts(state) || {};

  const getPropsForElement = element => getElement(state, element);
  const getPropsForMdElement = element => getMdElement(state, element);

  const elements = [
    'body', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'button', 'a', 'input', 'img',
  ].reduce(createElementStylesMap({ fonts, getProps: getPropsForElement, defaultFont: true }), {});

  const button = getElement(state, 'button');
  const {
    backgroundColor, hoverBackgroundColor, activeBackgroundColor, activeBoxShadow, borderRadius,
    textAlign, textTransform, fontSize, lineHeight, letterSpacing, color,
  } = button;

  // Main button styles
  const buttonBaseStyles = [
    backgroundColor && `background-color: ${backgroundColor} !important;`,
    borderRadius && `border-radius: ${borderRadius} !important;`,
    textAlign && `text-align: ${textAlign} !important;`,
    textTransform && `text-transform: ${textTransform} !important;`,
    fontSize && `font-size: ${fontSize} !important;`,
    lineHeight && `line-height: ${lineHeight} !important;`,
    letterSpacing && `letter-spacing: ${letterSpacing} !important;`,
    color && `color: ${color} !important;`,
  ].filter(Boolean).join(' ');

  const buttonStateStylesObj = {
    hover: [
      hoverBackgroundColor && `background-color: ${hoverBackgroundColor} !important;`,
    ],
    active: [
      activeBackgroundColor && `background-color: ${activeBackgroundColor} !important;`,
      activeBoxShadow && `box-shadow: ${activeBoxShadow} !important;`,
    ],
  };

  const buttonStateStyles = Object.keys(buttonStateStylesObj).reduce((arr, key) => {
    const filteredStyles = buttonStateStylesObj[key].filter(Boolean);
    if (!filteredStyles.length) {
      return arr;
    }
    const stylesString = filteredStyles.join(' ');
    return [
      ...arr,
      `.theme .themed-button:${key} { ${stylesString} }`,
    ];
  }, []).join(' ');

  const link = getElement(state, 'a');
  const { hoverColor, hoverTextDecoration, visitedColor, visitedTextDecoration } = link;

  const linkStateStylesObj = {
    hover: [
      hoverTextDecoration && `text-decoration: ${hoverTextDecoration} !important;`,
      hoverColor && `color: ${hoverColor} !important;`,
    ],
    visited: [
      visitedTextDecoration && `text-decoration: ${visitedTextDecoration} !important;`,
      visitedColor && `color: ${visitedColor} !important;`,
    ],
  };

  const linkStateStyles = Object.keys(linkStateStylesObj).reduce((arr, key) => {
    const filteredStyles = linkStateStylesObj[key].filter(Boolean);
    if (!filteredStyles.length) {
      return arr;
    }
    const stylesString = filteredStyles.join(' ');
    return [
      ...arr,
      `.theme .rnl-Rte-block--p a:${key} { ${stylesString} }`,
      `.theme li a.rnl-EditorState-Link:${key} { ${stylesString} }`,
    ];
  }, []).join(' ');

  const mdElements = [
    'h1', 'h2', 'h3',
  ].reduce(createElementStylesMap({ fonts, getProps: getPropsForMdElement }), {});

  const mdClasses = [
    mdElements.h1 && `.theme h1 { ${mdElements.h1} }`,
    mdElements.h2 && `.theme h2 { ${mdElements.h2} }`,
    mdElements.h3 && `.theme h3 { ${mdElements.h3} }`,
  ].filter(Boolean).join('\n');

  // The RTE inserts newlines as an entirely new RTE block, however, SM's content import has a lot of
  // newlines that are not an entirely new RTE block. Without this styling, the site looks off on several pages.
  // So, to preserve those newlines, we only apply white-space styling to the .rnl-Content class
  const body = getElement(state, 'body');
  const { whiteSpace } = body;
  const rnlContentBodyStyles = [
    whiteSpace && `.theme .rnl-Content { white-space: ${whiteSpace} !important }`,
  ];

  return [
    elements.body && `.theme p, .theme, .modal-dialog, .rnl-Rte-block--p, .theme li { ${elements.body} }`,
    elements.h1 && `.theme h1 { ${elements.h1} }`,
    elements.h2 && `.theme h2 { ${elements.h2} }`,
    elements.h3 && `.theme h3 { ${elements.h3} }`,
    elements.h4 && `.theme h4 { ${elements.h4} }`,
    elements.h5 && `.theme h5 { ${elements.h5} }`,
    elements.h6 && `.theme h6 { ${elements.h6} }`,
    elements.a && `.theme .rnl-Rte-block--p a, .theme li a.rnl-EditorState-Link { ${elements.a} }`,
    elements.img && `.theme img { ${elements.img} }`,
    elements.input && `.theme input { ${elements.input} }`,
    elements.blockquote && `.theme blockquote { ${elements.blockquote} }`,
    elements.button && `.theme .themed-button { ${buttonBaseStyles} }`, // Apply main button styles
    buttonStateStyles,
    linkStateStyles,
    rnlContentBodyStyles,
    mdClasses && `@media screen and (max-width: 768px) { ${mdClasses} }`,
  ].filter(Boolean).join('\n');
};
